import { Link } from 'react-router-dom';
import { DocSection, Callout, kbd } from '../../../pages/shell';
import { DocShot } from '../../DocShot';

export const toc = [
  { id: 'visual', label: 'Visual model' },
  { id: 'speech', label: 'Speech models' },
  { id: 'face', label: 'Face model' },
  { id: 'ultra', label: 'Ultra query-parser models' },
  { id: 'downloads', label: 'Download sizes & where they live' },
];

export default function Body() {
  return (
    <>
      <DocSection id="visual" title="Visual model">
        <p>
          ClipRadar's Visual pass runs on a single model: OpenCLIP{' '}
          <code>ViT-SO400M-16-SigLIP2-512</code> (the <code>webli</code> checkpoint), downloaded
          once from Hugging Face. It's what turns every scene into the embedding that Visuals and
          Ultra Search compare against.
        </p>
        <p>
          In <span style={kbd}>Settings → Models</span> it shows up as <strong>read-only</strong> —
          there's no picker, because every embedding in your library has to come from the same
          model to stay comparable.
        </p>
        <DocShot
          slug="models"
          shot="settings-models"
          alt="Settings → Models, showing the Visual model row, the Speech model picker, and download states"
          caption="Settings → Models. Each row shows a live download state — Downloaded, Downloading, or Failed with a Retry."
          callouts={[
            { n: 1, label: 'Visual model — read-only, always the same model' },
            { n: 2, label: 'Speech model — pick a different size here' },
            { n: 3, label: 'Speech device — GPU (CUDA) compute status' },
            { n: 4, label: 'Check transcripts — a scan for unreliable transcripts, not a job' },
          ]}
        />
      </DocSection>

      <DocSection id="speech" title="Speech models">
        <p>
          Speech transcription runs on faster-whisper. The default is{' '}
          <code>Large v3 Turbo</code> — fast, and accurate enough for most footage. In{' '}
          <span style={kbd}>Settings → Models</span> you can pick a different size instead:{' '}
          <code>Large v3</code> (the full model — more accurate, slower), <code>Medium</code>,{' '}
          <code>Small</code>, or <code>Base</code> (fastest, least accurate).
        </p>
        <p>
          One exception: for Bulgarian audio, ClipRadar can automatically upgrade from Turbo to
          the full <code>Large v3</code> model for better accuracy — but only when that model is
          already downloaded on your machine and there's enough free VRAM to spare. It never
          downloads the larger model on its own just to do this.
        </p>
        <p>
          Speech also has its own <strong>Speech device</strong> row in{' '}
          <span style={kbd}>Settings → Models</span> — a compute-status readout, not a choice to
          make. It reflects the same NVIDIA GPU (CUDA) that every other pass — Visual analysis,
          Faces, and Ultra Search — requires to run.
        </p>
      </DocSection>

      <DocSection id="face" title="Face model">
        <p>
          Faces uses InsightFace's <code>buffalo_l</code> model for both detecting faces and
          embedding them — the model behind clustering, naming people, and Face Collections. It
          downloads automatically the first time you run a Faces pass. Unlike the other models
          here, it comes from InsightFace's own GitHub releases rather than Hugging Face, which
          matters if your network only allows one of the two.
        </p>
      </DocSection>

      <DocSection id="ultra" title="Ultra query-parser models">
        <p>
          Ultra Search's query parser is a small local LLM that turns a natural-language query
          into structured chips — <code>Qwen3-4B</code> (instruct) by default, or a smaller{' '}
          <strong>Lite</strong> size for lighter cards. Both download automatically the first time
          you turn on Ultra Search, from{' '}
          <span style={kbd}>Settings → Ultra Search</span>, which also has a{' '}
          <strong>GPU memory policy</strong> (Automatic / Keep ready / Load on demand) that
          controls whether the parser stays warm in VRAM between searches.
        </p>
        <Callout kind="warn">
          The query-parser model is GPU-only — there's no CPU fallback for it. On a card without
          enough free VRAM, Ultra Search falls back to plain search rather than running the parser
          on CPU. See <Link to="/docs/concepts/ultra-search">Ultra Search</Link> for the full
          fallback behavior.
        </Callout>
      </DocSection>

      <DocSection id="downloads" title="Download sizes & where they live">
        <p>
          All told, ClipRadar's models add up to roughly <strong>~13 GB</strong>. The Visual,
          Speech, and Faces models — plus BAAI's <code>bge-m3</code>, which powers the semantic
          side of transcript search and doesn't have its own Settings row — download in the
          background on first launch. The Ultra query-parser model is a separate download,
          triggered the first time you turn on Ultra Search from{' '}
          <span style={kbd}>Settings → Ultra Search</span>.
        </p>
        <p>
          Everything lands in your local models folder, inside ClipRadar's data directory. See{' '}
          <Link to="/docs/reference/where-your-data-lives">Where ClipRadar stores things</Link> for
          the exact path.
        </p>
      </DocSection>
    </>
  );
}
